import { useEffect, useMemo, useState } from 'react';
import { AiPrediction, Linkrole, DocumentTaxonomyConfig, PresentationTreeResponse, UpdateDocumentTaxonomyConfigInput } from 'entities/tag';
import { buildLinkroleTree, collectTreeTokens, prefillSelection, keepLinkroleForConcept, buildAncestorIndex, parseToken } from './aiLinkroleFilter';

interface UseAiLinkroleFilterArgs {
  show: boolean;
  config: DocumentTaxonomyConfig | null | undefined;
  presentationTree: PresentationTreeResponse | null | undefined;
  tagLanguage: string;
  taxonomyId: string | null;
  referenceDataSuperId: string;
  updateConfig: (input: UpdateDocumentTaxonomyConfigInput) => void;
}

/**
 * Owns the AI modal's linkrole-filter state and derivations: the override map,
 * the tree/tokens, the current selection (prefilled from the saved config), and
 * the helpers to filter a prediction's linkroles and persist the chosen filter.
 * Extracted from AiApplicationModal to keep that component render-focused.
 */
export function useAiLinkroleFilter({ show, config, presentationTree, tagLanguage, taxonomyId, referenceDataSuperId, updateConfig }: UseAiLinkroleFilterArgs) {
  const linkroleOverrides = useMemo<Record<string, { alias?: string; hidden?: boolean }>>(
    () => config?.taxonomyOverrides?.[taxonomyId ?? ''] ?? {},
    [config, taxonomyId]
  );

  const linkroleTree = useMemo(
    () => buildLinkroleTree(presentationTree, linkroleOverrides, tagLanguage ?? 'sv'),
    [presentationTree, linkroleOverrides, tagLanguage]
  );
  const linkroleTokenValues = useMemo(() => collectTreeTokens(linkroleTree), [linkroleTree]);
  const ancestorIndex = useMemo(() => buildAncestorIndex(presentationTree), [presentationTree]);
  const [selectedLinkroles, setSelectedLinkroles] = useState<string[]>([]);
  const selectedTokens = useMemo(() => selectedLinkroles.map(parseToken), [selectedLinkroles]);

  useEffect(() => {
    if (!show) return;
    setSelectedLinkroles(prefillSelection(config?.aiLinkroleFilters?.[referenceDataSuperId], linkroleTokenValues));
  }, [show, config, referenceDataSuperId, linkroleTokenValues]);

  const toggleLinkrole = (uri: string) => setSelectedLinkroles((prev) => (prev.includes(uri) ? prev.filter((u) => u !== uri) : [...prev, uri]));

  const linkroleRows = (p: AiPrediction): Linkrole[] =>
    (p.linkroles ?? [])
      .filter((lr) => linkroleOverrides[lr.uri]?.hidden !== true)
      .filter((lr) => keepLinkroleForConcept(lr, p.conceptName, selectedTokens, ancestorIndex));

  const persistLinkroleFilter = () => {
    if (!config?.id) return;
    const existing = config.aiLinkroleFilters?.[referenceDataSuperId] ?? [];
    const sameAsSaved = existing.length === selectedLinkroles.length && existing.every((u: string) => selectedLinkroles.includes(u));
    if (sameAsSaved) return;
    updateConfig({
      configId: config.id,
      silent: true,
      aiLinkroleFilters: { ...(config.aiLinkroleFilters ?? {}), [referenceDataSuperId]: selectedLinkroles },
    });
  };

  return { linkroleOverrides, linkroleTree, ancestorIndex, selectedLinkroles, selectedTokens, toggleLinkrole, linkroleRows, persistLinkroleFilter };
}
